// decision-ledger-projection-helpers.ts — hàm phụ thuần của
// decision-ledger-projection.ts (tách theo review 03b L8: file mẹ 194/200 dòng,
// phase 07 sẽ mở rộng). Đọc field journal kiểu lỏng, chuẩn hoá chuỗi qua
// redactor; câu chờ dự phòng từ interview.json (review 03b M1).

import type { PendingDecisionOption, PendingDecisionQuestion } from "./decision-ledger-types.js";
import { redactDecisionText } from "./decision-policy-journal-events.js";
import { needsDecisionQuestionTicket } from "./needs-decision-question-ticket-match.js";
import type { Interview } from "./schemas/interview-schema.js";

export interface AskedSnapshot {
  ticketId?: string;
  summary: string;
  options: PendingDecisionOption[];
  recommendedOptionId?: string;
}

export const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
// Chỉ pattern: text đến từ journal (đã che known-values ở `MissionStore.commit`)
// VÀ `interview.json` (che known-values lúc HỎI, chưa cắt). Secret xuất hiện SAU
// lúc hỏi trong title > 4000 ký tự có thể lộ tiền tố sau cắt — backlog review L1.
export const clean = (text: string): string => redactDecisionText(text, []);

export function askedSnapshot(delta: Record<string, unknown>): AskedSnapshot {
  const options = Array.isArray(delta.options) ? delta.options : [];
  return {
    ticketId: str(delta.ticket_id),
    summary: str(delta.summary) ?? "",
    options: options.flatMap((o: unknown) => {
      const rec = (o ?? {}) as Record<string, unknown>;
      const label = str(rec.label);
      if (label === undefined) return [];
      const id = str(rec.id);
      return [{ ...(id === undefined ? {} : { id }), label: clean(label) }];
    }),
    recommendedOptionId: str(delta.recommended_option_id),
  };
}

/** Câu chọn ở `title|summary|decision`, không có thì JSON gọn của record. */
export function workerDecisionText(record: unknown): string {
  const rec = (record ?? {}) as Record<string, unknown>;
  const text = str(rec.title) ?? str(rec.summary) ?? str(rec.decision);
  return clean(text ?? JSON.stringify(record));
}

export function answeredLabel(value: unknown): string | undefined {
  if (Array.isArray(value))
    return value.filter((v) => typeof v === "string").join(", ") || undefined;
  return str(value);
}

export function dropUndefined<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}

/**
 * Review 03b M1: mission bắt đầu ở 3.34.x có câu chờ CHỈ trong interview.json
 * (không có `decision_question_asked`). Câu chưa answer, journal chưa hỏi/chốt
 * ⇒ vẫn là câu chờ: text = `title`, options + `option_ids` (thiếu id ⇒ chỉ nhãn).
 */
export function pendingFromInterviewOnly(
  interview: Interview,
  known: ReadonlySet<string>,
): PendingDecisionQuestion[] {
  return interview.questions
    .filter((q) => !known.has(q.id) && interview.answers[q.id] === undefined)
    .map((q) =>
      dropUndefined({
        questionId: q.id,
        ticketId: needsDecisionQuestionTicket(q),
        question: clean(q.title),
        options: (q.options ?? []).map((label, i): PendingDecisionOption => {
          const id = q.option_ids?.[i];
          return id === undefined ? { label: clean(label) } : { id, label: clean(label) };
        }),
      }),
    );
}
